import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { FILTER_KEY_APPLICATION } from 'app/constants/filters';
import { set } from '../../../../../../lib/session';
import Creative from '../../../../../../models/Creative';
import Trend from '../../../../../Templates/Reports/Trend/Trend';
import Empty from '../../../../../Templates/Reports/Empty/Empty';
import FiltersCache from '../../../../../../lib/cache/FiltersCache';
import { metricsGroup } from '../../../../../../components/V2/Report/data/Metrics';
import { QUICK_KEY_LAST_7_DAYS } from '../../../../../../components/V2/Report/Calendar/PickerQuick';
import {
  getCacheValue,
  setCacheValue,
  combineRequestBody,
  getDefaultCacheValues,
} from '../../../../../Templates/Reports/helper';
import { reportsExport } from '../../../../../../app/graphql/utils/reporting';
import {
  DATE_KEY, COMPARE_KEY, COMBINE_KEY, GRAPHQL_QUERIES, REPORTS_KEY_REFRESH, REPORTS_KEY_RESET,
} from '../../../../../Templates/Reports/constant';
import CombinedFilters from '../../../../../../components/V2/Filters/CombinedFilters/CombinedFilters';
import { APPLICATION_CREATIVE_LIST_CACHE_KEY } from '../../../../../../lib/cache/constant';
import { CREATE_CREATIVE_SOURCE_KEY } from '../../../creatives/Edit/constant';
import CreativeDataTable, { initMetrics } from '../Components/DataTable/CreativeDataTable/CreativeDataTable';
import DateRangePicker from '../Components/DateRangePicker/DateRangePicker';
import { METRIC_KEY_ID } from '../Components/DataTable/constants';
import paths from '../../../../paths';
import {
  initCheckedDate, initCheckedCombine, initCombineFilters, initCompareFilters, combineFiltersCacheData, detectDataEmpty,
} from '../helper';
import './creativeList.scss';

const dimension = 'creative';

export const defaultCacheValues = {
  [COMPARE_KEY]: {},
  [COMBINE_KEY]: initCheckedCombine(dimension),
  [DATE_KEY]: initCheckedDate(QUICK_KEY_LAST_7_DAYS),
};
const {
  CREATE_CREATIVES: {
    getPath: getPathToCreateCreatives,
  },
} = paths;

const CreativeList = ({
  node,
  authUser,
  match,
  history,
  reportsCache,
  actions: {
    addCreative: addCreativeAction,
    changeCreativeStatus,
  },
}, ref) => {
  const trendRef = useRef();
  const tableRef = useRef();
  const filterContainerRef = useRef();

  const [paddingTop, setPaddingTop] = useState(0);

  const cache = useMemo(() => new FiltersCache(authUser.id, APPLICATION_CREATIVE_LIST_CACHE_KEY, defaultCacheValues), [authUser.id]);

  const [dataEmpty, setDataEmpty] = useState(false);
  const [checkedDate, setCheckedDate] = useState(getCacheValue(cache, DATE_KEY));
  const [checkedCombine, setCheckedCombine] = useState(getCacheValue(cache, COMBINE_KEY));
  const [checkedCompare, setCheckedCompare] = useState(getCacheValue(cache, COMPARE_KEY));

  const resizeBlank = useCallback(() => {
    if (filterContainerRef.current) {
      setPaddingTop(filterContainerRef.current.clientHeight + 8);
    }
  }, [filterContainerRef]);

  useEffect(() => {
    const { account } = authUser;
    const application = node.get('id');
    detectDataEmpty(dimension, { account, application })
      .then((empty) => {
        setDataEmpty(empty);
      });

    resizeBlank();
    window.addEventListener('resize', resizeBlank);
    return () => window.removeEventListener('resize', resizeBlank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() => {
    trendRef.current.onLoadChartData();
    tableRef.current.onLoadTableData();
  }, []);

  const addCreative = useCallback(async () => {
    const creative = new Creative({
      id: 'create',
      account: { ...authUser },
    });
    if (node.isSKAdNetworkEnabled()) {
      const skadNetworkSettings = await node.getSkadnetworkSettings();
      if (skadNetworkSettings?.usage) {
        node.set('skadnetwork_settings.usage.remaining', skadNetworkSettings.usage.remaining || 0);
      }
    }
    creative.set('application', node);
    addCreativeAction({
      creative,
      [CREATE_CREATIVE_SOURCE_KEY]: `/applications/${node.get('id')}/creatives`,
    });
    history.push(getPathToCreateCreatives());
  }, [authUser, node, addCreativeAction, history]);

  const viewReport = useCallback(() => {
    const prevCache = getCacheValue(reportsCache, COMBINE_KEY) || getDefaultCacheValues()[COMBINE_KEY];
    const cacheData = combineFiltersCacheData(prevCache, node, checkedCombine);
    setCacheValue(reportsCache, COMBINE_KEY, cacheData);
    setCacheValue(reportsCache, DATE_KEY, checkedDate);
    set('cache', true);
    history.push('/reports');
  }, [checkedCombine, checkedDate, history, node, reportsCache]);

  const downloadCSV = useCallback(async () => {
    const { id } = match.params;
    const metrics = initMetrics(authUser.id);
    const body = combineRequestBody({
      checkedDate,
      checkedCompare,
      checkedCombine,
      dimensions: [dimension],
      filter_applications: [id],
      // reporting export api doesn't support id field
      metrics: metrics.map((metric) => metric.key).filter((key) => key !== METRIC_KEY_ID),
    });
    const resp = await reportsExport(body, dimension);
    window.open(resp.location, '_blank', 'noopener');
  }, [match.params, authUser.id, checkedDate, checkedCompare, checkedCombine]);

  const clearCache = useCallback(() => {
    cache.save(defaultCacheValues);
  }, [cache]);

  const executeDropdown = useCallback((e) => {
    if (REPORTS_KEY_RESET === e.key) {
      clearCache();
      setCheckedDate(defaultCacheValues[DATE_KEY]);
      setCheckedCompare(defaultCacheValues[COMPARE_KEY]);
      setCheckedCombine(defaultCacheValues[COMBINE_KEY]);
    }
    if (REPORTS_KEY_REFRESH === e.key) {
      refresh();
    }
  }, [clearCache, refresh]);

  useImperativeHandle(ref, () => ({
    clearCache,
    addCreative,
    refresh,
    viewReport,
    downloadCSV,
    executeDropdown,
  }));

  const onCombineChange = useCallback((e) => {
    setCheckedCombine(e);
    setCacheValue(cache, COMBINE_KEY, e);
  }, [cache]);

  const onCompareChange = useCallback((e) => {
    setCheckedCompare(e);
    setCacheValue(cache, COMPARE_KEY, e);
  }, [cache]);

  const onDateChange = useCallback((e) => {
    setCheckedDate(e);
    setCacheValue(cache, DATE_KEY, e);
  }, [cache]);

  return (
    <div className="creative_list_container">
      <div ref={filterContainerRef} className="filter_container">
        <CombinedFilters
          dropdownFixed
          filters={initCombineFilters(node, dimension)}
          defaultCheckedFilters={checkedCombine}
          checkedFilters={checkedCombine}
          onFilterListLengthChange={resizeBlank}
          onChange={onCombineChange}
        />
        <DateRangePicker checkedDate={checkedDate} onChange={onDateChange} />
      </div>
      {dataEmpty ? <Empty dimension={dimension} onAdd={addCreative} description="Your application looks a little empty. Let’s add our first creative." /> : (
        <div className="content" style={{ paddingTop }}>
          <Trend
            ref={trendRef}
            checkedDate={checkedDate}
            checkedCombine={checkedCombine}
            checkedCompare={checkedCompare}
            compareFilters={initCompareFilters(dimension)}
            metricOptionGroup={[metricsGroup[0]]}
            searchParams={{ application: node.get('id') }}
            requestParams={{ [GRAPHQL_QUERIES[FILTER_KEY_APPLICATION].filterQuery]: [node.get('id')] }}
            onCompareChange={(key, data) => onCompareChange({ key, data })}
            onCompareClear={() => onCompareChange(defaultCacheValues[COMPARE_KEY])}
            dimension={dimension}
          />
          <CreativeDataTable
            ref={tableRef}
            node={node}
            dimension={dimension}
            checkedDate={checkedDate}
            checkedCombine={checkedCombine}
            changeCreativeStatus={changeCreativeStatus}
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(forwardRef(CreativeList));
